import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  getAllStudents, createStudent,
  bulkUploadStudents, updateStudent, deleteStudent, getCourses,
  adminCreatePassForStudent
} from "../lib/api";
import { 
  Plus, Pencil, Trash2, Loader2, Upload, Download, Search, 
  Users, GraduationCap, Ban, FileText
} from "lucide-react";
import WebcamCapture from "../components/WebcamCapture";

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCourse, setFilterCourse] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingReg, setGeneratingReg] = useState(false);
  
  // Bulk upload
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkData, setBulkData] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Admin create pass for blocked student
  const [showPassDialog, setShowPassDialog] = useState(false);
  const [passStudent, setPassStudent] = useState(null);
  const [passSubmitting, setPassSubmitting] = useState(false);
  const [studentPhoto, setStudentPhoto] = useState(null);
  const [passFormData, setPassFormData] = useState({
    pass_type: "",
    reason: "",
    exit_date: "",
    exit_time: "",
    expected_return_date: "",
    expected_return_time: "",
    destination: "",
    emergency_contact: "",
    student_remarks: "",
  });
  
  // Form data
  const [formData, setFormData] = useState({
    registration_number: "",
    name: "",
    father_name: "",
    course_id: "",
    year: "",
    mobile_number: "",
    hostel_status: "hostel",
    parent_phone: "",
    pass_allowed: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [studentsRes, coursesRes] = await Promise.all([
        getAllStudents(),
        getCourses()
      ]);
      setStudents(studentsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStudent = async () => {
    if (!formData.registration_number || !formData.name || !formData.father_name || 
        !formData.course_id || !formData.year || !formData.mobile_number) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitting(true);
    try {
      if (editingStudent) {
        await updateStudent(editingStudent.id, {
          name: formData.name,
          father_name: formData.father_name,
          mobile_number: formData.mobile_number,
          parent_phone: formData.parent_phone,
          hostel_status: formData.hostel_status,
          pass_allowed: formData.pass_allowed
        });
        toast.success("Student updated!");
      } else {
        await createStudent({
          ...formData,
          year: parseInt(formData.year),
          pass_allowed: formData.pass_allowed
        });
        toast.success("Student created!");
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save student");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    
    try {
      await deleteStudent(studentId);
      toast.success("Student deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete student");
    }
  };

  const openPassDialog = (student) => {
    setPassStudent(student);
    setPassFormData({
      pass_type: "", reason: "", exit_date: "", exit_time: "",
      expected_return_date: "", expected_return_time: "",
      destination: "", emergency_contact: student.mobile_number || "", student_remarks: "",
    });
    setStudentPhoto(null);
    setShowPassDialog(true);
  };

  const handleAdminCreatePass = async () => {
    if (!passFormData.pass_type || !passFormData.reason || !passFormData.exit_date || !passFormData.exit_time || !passFormData.destination) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!studentPhoto) {
      toast.error("Student photo is mandatory. Please capture a photo using the webcam.");
      return;
    }
    setPassSubmitting(true);
    try {
      await adminCreatePassForStudent({
        registration_number: passStudent.registration_number,
        ...passFormData,
        student_photo: studentPhoto
      });
      toast.success("Gate pass created successfully for restricted student!");
      setShowPassDialog(false);
      setPassStudent(null);
      setStudentPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pass");
    } finally {
      setPassSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      registration_number: "",
      name: "",
      father_name: "",
      course_id: "",
      year: "",
      mobile_number: "",
      hostel_status: "hostel",
      parent_phone: "",
      pass_allowed: true
    });
    setEditingStudent(null);
  };

  const openEditDialog = (student) => {
    setEditingStudent(student);
    setFormData({
      registration_number: student.registration_number,
      name: student.name,
      father_name: student.father_name,
      course_id: student.course_id,
      year: student.year?.toString(),
      mobile_number: student.mobile_number,
      hostel_status: student.hostel_status || "hostel",
      parent_phone: student.parent_phone || "",
      pass_allowed: student.pass_allowed !== false
    });
    setShowDialog(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setBulkData(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (!bulkData.trim()) {
      toast.error("No data to upload");
      return;
    }

    // Parse CSV data
    const lines = bulkData.trim().split("\n");
    const students = [];
    
    for (let i = 1; i < lines.length; i++) { // Skip header
      const parts = lines[i].split(",").map(p => p.trim());
      if (parts.length >= 6) {
        const passAllowedVal = (parts[8] || "YES").toUpperCase();
        students.push({
          registration_number: parts[0],
          name: parts[1],
          father_name: parts[2],
          course_id: parts[3],
          year: parseInt(parts[4]),
          mobile_number: parts[5],
          hostel_status: parts[6] || "hostel",
          parent_phone: parts[7] || parts[5],
          pass_allowed: passAllowedVal !== "NO" && passAllowedVal !== "FALSE" && passAllowedVal !== "0"
        });
      }
    }

    if (students.length === 0) {
      toast.error("No valid student records found");
      return;
    }

    setBulkUploading(true);
    try {
      const result = await bulkUploadStudents(students);
      if (result.data.failed > 0 && result.data.errors?.length > 0) {
        toast.error(`Upload: ${result.data.success} success, ${result.data.failed} failed. Errors: ${result.data.errors.slice(0, 3).join("; ")}`, { duration: 8000 });
      } else {
        toast.success(`Upload complete: ${result.data.success} students added successfully!`);
      }
      if (result.data.success > 0) {
        setShowBulkDialog(false);
        setBulkData("");
      }
      loadData();
    } catch (error) {
      toast.error("Bulk upload failed");
    } finally {
      setBulkUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = "Registration No,Student Name,Father Name,Course,Year,Mobile No,Hostel Status,Parent Phone,Pass Allowed\nREG001,John Doe,Robert Doe,GNM,1,9876543210,hostel,9876543211,YES\nREG002,Jane Smith,David Smith,B.Sc Nursing,2,9876543220,day_scholar,9876543221,NO";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student_upload_template.csv";
    a.click();
  };

  // Filter students
  const filteredStudents = students.filter(s => {
    const matchSearch = !searchQuery || 
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.registration_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.father_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCourse = filterCourse === "all" || s.course_id === filterCourse;
    const matchYear = filterYear === "all" || s.year?.toString() === filterYear;
    return matchSearch && matchCourse && matchYear;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-management">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Students</p>
                <p className="text-2xl font-semibold">{students.length}</p>
              </div>
              <Users className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-semibold text-emerald-600">
                  {students.filter(s => s.is_active !== false).length}
                </p>
              </div>
              <GraduationCap className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Courses</p>
                <p className="text-2xl font-semibold">{courses.length}</p>
              </div>
              <GraduationCap className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions & Filters */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle>Student Registry</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBulkDialog(true)} data-testid="bulk-upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-student-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Student
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name or registration number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={filterCourse} onValueChange={setFilterCourse}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                <SelectItem value="1">Year 1</SelectItem>
                <SelectItem value="2">Year 2</SelectItem>
                <SelectItem value="3">Year 3</SelectItem>
                <SelectItem value="4">Year 4</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Registration No.</TableHead>
                  <TableHead>Student Name</TableHead>
                  <TableHead>Father Name</TableHead>
                  <TableHead>Course / Year</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No students found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.slice(0, 50).map((student) => (
                    <TableRow key={student.id} data-testid={`student-row-${student.id}`}>
                      <TableCell className="font-mono text-sm font-medium">
                        {student.registration_number}
                      </TableCell>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.father_name}</TableCell>
                      <TableCell>
                        {student.course_name} - Y{student.year}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{student.mobile_number}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Badge variant={student.is_active !== false ? "default" : "secondary"}>
                            {student.is_active !== false ? "Active" : "Inactive"}
                          </Badge>
                          {student.pass_allowed === false && (
                            <Badge variant="destructive" className="flex items-center gap-0.5" data-testid={`not-allowed-badge-${student.id}`}>
                              <Ban className="h-3 w-3" /> No Pass
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {student.pass_allowed === false && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => openPassDialog(student)}
                              title="Create pass (Admin override)"
                              data-testid={`create-pass-${student.id}`}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditDialog(student)}
                            data-testid={`edit-student-${student.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteStudent(student.id)}
                            data-testid={`delete-student-${student.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filteredStudents.length > 50 && (
            <p className="text-sm text-slate-500 mt-2">Showing 50 of {filteredStudents.length} students</p>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Student Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingStudent ? "Edit Student" : "Add New Student"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!editingStudent && (
              <>
                <div className="space-y-2">
                  <Label>Registration Number *</Label>
                  <Input
                    value={formData.registration_number}
                    onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                    placeholder="Enter registration number manually"
                    data-testid="registration-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Course *</Label>
                    <Select 
                      value={formData.course_id} 
                      onValueChange={(v) => setFormData({...formData, course_id: v})}
                    >
                      <SelectTrigger data-testid="course-select">
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year *</Label>
                    <Select 
                      value={formData.year} 
                      onValueChange={(v) => setFormData({...formData, year: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map(y => (
                          <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Student Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter student name"
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Father's Name *</Label>
              <Input
                value={formData.father_name}
                onChange={(e) => setFormData({...formData, father_name: e.target.value})}
                placeholder="Enter father's name"
                data-testid="father-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mobile Number *</Label>
                <Input
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                  placeholder="9876543210"
                  data-testid="mobile-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Parent Phone</Label>
                <Input
                  value={formData.parent_phone}
                  onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
                  placeholder="Parent phone (optional)"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Hostel Status</Label>
              <Select 
                value={formData.hostel_status} 
                onValueChange={(v) => setFormData({...formData, hostel_status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hostel">Hostel</SelectItem>
                  <SelectItem value="day_scholar">Day Scholar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg" data-testid="pass-allowed-toggle">
              <div>
                <p className="text-sm font-medium">Pass Allowed</p>
                <p className="text-xs text-slate-500">
                  {formData.pass_allowed ? "Student can request gate passes" : "Student is restricted from requesting passes (Admin only)"}
                </p>
              </div>
              <Switch
                checked={formData.pass_allowed}
                onCheckedChange={(v) => setFormData({...formData, pass_allowed: v})}
                data-testid="pass-allowed-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveStudent} disabled={submitting} data-testid="save-student-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingStudent ? "Update Student" : "Add Student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload Students</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                Upload a CSV file with student data. Required columns:
              </p>
              <p className="text-xs font-mono text-blue-600">
                Registration No, Student Name, Father Name, Course ID, Year, Mobile No, Hostel Status, Parent Phone, Pass Allowed (YES/NO)
              </p>
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 h-auto mt-2"
                onClick={downloadTemplate}
              >
                <Download className="h-3 w-3 mr-1" />
                Download Template
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                ref={fileInputRef}
              />
            </div>

            <div className="space-y-2">
              <Label>Or Paste CSV Data</Label>
              <textarea
                className="w-full h-40 p-3 border rounded-lg font-mono text-sm"
                placeholder="Registration No,Student Name,Father Name,Course,Year,Mobile No,Hostel Status,Parent Phone,Pass Allowed
REG001,John Doe,Robert Doe,GNM,1,9876543210,hostel,9876543211,YES"
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
              />
            </div>

            <div className="p-3 bg-slate-50 rounded text-sm">
              <p className="font-medium">Course IDs:</p>
              <p className="text-slate-500">
                gnm, bsc_nursing, post_basic_nursing, msc_nursing, bpt, mpt, npcc
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkUpload} disabled={bulkUploading || !bulkData.trim()} data-testid="upload-btn">
              {bulkUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Create Pass Dialog (for blocked students) */}
      <Dialog open={showPassDialog} onOpenChange={setShowPassDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Admin: Create Gate Pass
            </DialogTitle>
          </DialogHeader>
          
          {passStudent && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="font-medium text-amber-800">Admin Override</p>
                <p className="text-amber-700 text-xs">This student is marked as "Pass Not Allowed". Only Admin can create a pass.</p>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg text-sm grid grid-cols-2 gap-2">
                <div>
                  <p className="text-slate-500">Student</p>
                  <p className="font-medium">{passStudent.name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Registration</p>
                  <p className="font-mono font-medium">{passStudent.registration_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Course/Year</p>
                  <p>{passStudent.course_name} - Year {passStudent.year}</p>
                </div>
                <div>
                  <p className="text-slate-500">Father</p>
                  <p>{passStudent.father_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pass Type *</Label>
                  <Select value={passFormData.pass_type} onValueChange={(v) => setPassFormData({...passFormData, pass_type: v})}>
                    <SelectTrigger data-testid="admin-pass-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short_leave">Short Leave</SelectItem>
                      <SelectItem value="day_pass">Day Pass</SelectItem>
                      <SelectItem value="emergency_leave">Emergency Leave</SelectItem>
                      <SelectItem value="home_visit">Home Visit</SelectItem>
                      <SelectItem value="medical_leave">Medical Leave</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination *</Label>
                  <Input
                    value={passFormData.destination}
                    onChange={(e) => setPassFormData({...passFormData, destination: e.target.value})}
                    placeholder="Where is student going?"
                    data-testid="admin-pass-destination"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Exit Date *</Label>
                  <Input type="date" value={passFormData.exit_date} onChange={(e) => setPassFormData({...passFormData, exit_date: e.target.value})} data-testid="admin-pass-exit-date" />
                </div>
                <div className="space-y-2">
                  <Label>Exit Time *</Label>
                  <Input type="time" value={passFormData.exit_time} onChange={(e) => setPassFormData({...passFormData, exit_time: e.target.value})} data-testid="admin-pass-exit-time" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Return Date</Label>
                  <Input type="date" value={passFormData.expected_return_date} onChange={(e) => setPassFormData({...passFormData, expected_return_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Return Time</Label>
                  <Input type="time" value={passFormData.expected_return_time} onChange={(e) => setPassFormData({...passFormData, expected_return_time: e.target.value})} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason *</Label>
                <Textarea
                  value={passFormData.reason}
                  onChange={(e) => setPassFormData({...passFormData, reason: e.target.value})}
                  placeholder="Reason for gate pass"
                  rows={2}
                  data-testid="admin-pass-reason"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Emergency Contact</Label>
                  <Input value={passFormData.emergency_contact} onChange={(e) => setPassFormData({...passFormData, emergency_contact: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Input value={passFormData.student_remarks} onChange={(e) => setPassFormData({...passFormData, student_remarks: e.target.value})} />
                </div>
              </div>

              <WebcamCapture
                onCapture={setStudentPhoto}
                onClear={() => setStudentPhoto(null)}
                capturedPhoto={studentPhoto}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPassDialog(false)}>Cancel</Button>
            <Button onClick={handleAdminCreatePass} disabled={passSubmitting} data-testid="admin-submit-pass-btn">
              {passSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              Create Gate Pass
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentManagement;
